import { Analytics } from "@vercel/analytics/react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { BottomSheet } from "react-spring-bottom-sheet";
import "react-spring-bottom-sheet/dist/style.css";
import supabase from "../../services/supabase";
import "./LocalMap.css";

// ----------------------
// Module-scope constants & helpers
// ----------------------
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const categories = ["Promoted", "Entertainment", "Hidden Gems", "Flowers", "Stargazing", "Eggheads"];
const transportTagOptions = ["car", "bike", "walking"];

const norCalBounds = [
  [-124.7, 36.3],
  [-120.0, 39.9],
];
const yoloBounds = [
  [-122.5, 38.2],
  [-121.3, 39.1],
];
const bayBounds = [
  [-123.2, 36.9],
  [-121.4, 38.7],
];
const bboxIntersects = (a, b) =>
  !(a[1][0] < b[0][0] || a[0][0] > b[1][0] || a[1][1] < b[0][1] || a[0][1] > b[1][1]);
const lngLatIn = (bbox, lng, lat) =>
  lng >= bbox[0][0] && lng <= bbox[1][0] && lat >= bbox[0][1] && lat <= bbox[1][1];

const categoryMarkerMap = {
  Promoted: "/promotedmarker.webp",
  Entertainment: "/popcornmarker.webp",
  "Hidden Gems": "/gemmarker.webp",
  Flowers: "/flowermarker.webp",
  Stargazing: "/starmarker.webp",
};

const categorySelectedMarkerMap = {
  Promoted: "/promotedmarker.webp",
  Entertainment: "/popcornmarker2.webp",
  "Hidden Gems": "/gemmarker2.webp",
  Flowers: "/flowermarker2.webp",
  Stargazing: "/starmarker2.webp",
};

const promotedCardImages = {
  "Kims Mart": "/kims.png",
  "Hunan Bar & Restaurant": "/hunan.png",
};

// Category aliases for hashtag -> category (omit "flowers" on purpose; #flowers stays a tag)
const categoryAliases = {
  entertainment: "Entertainment",
  hidden: "Hidden Gems",
  hiddengems: "Hidden Gems",
  stargazing: "Stargazing",
  eggheads: "Eggheads",
};

// Normalize & parsing helpers
const norm = (s) => (s ?? "").toString().trim().toLowerCase();
const parseHashtagsFromText = (text) =>
  [...((text || "").matchAll(/#([\p{L}\p{N}_-]+)/gu))].map((m) => norm(m[1])).filter(Boolean);
const parseCommaTokens = (text) => (text || "").split(",").map((s) => norm(s)).filter(Boolean);
const getSpotTags = (spot) => {
  const raw = Array.isArray(spot?.tags) ? spot.tags : typeof spot?.tags === "string" ? spot.tags.split(/[,:|]/) : [];
  return raw.map(norm).filter(Boolean);
};
const escapeHTML = (s = "") => s.replace(/[&<>"']/g, (c) => ({
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
}[c]));

// Camera tuning
const CAMERA = {
  nearPx: 120,
  minMs: 220,
  maxMs: 600,
  zoomAtDest: 19,
  offset: [0, -80],
};

function fastFlyTo(mapRef, lng, lat) {
  if (!mapRef) return;
  mapRef.stop();
  const from = mapRef.project(mapRef.getCenter());
  const to = mapRef.project([lng, lat]);
  const dist = Math.hypot(to.x - from.x, to.y - from.y);
  if (dist < CAMERA.nearPx) {
    mapRef.jumpTo({ center: [lng, lat], zoom: CAMERA.zoomAtDest, offset: CAMERA.offset });
    return;
  }
  const ms = Math.max(CAMERA.minMs, Math.min(CAMERA.maxMs, dist * 0.6));
  mapRef.easeTo({
    center: [lng, lat],
    zoom: CAMERA.zoomAtDest,
    duration: ms,
    offset: CAMERA.offset,
    easing: (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
  });
}

// --- NEW: distance helpers ---
const toRad = (d) => (d * Math.PI) / 180;
const haversineKm = (a, b) => {
  if (!a || !b) return Infinity;
  const R = 6371; // km
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s1 = Math.sin(dLat / 2) ** 2;
  const s2 = Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s1 + s2));
};

export default function LocalMap({ setSearchHandler, compact = false, initialCenter, initialZoom, minZoom }) {
  const [map, setMap] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);

  const [allData, setAllData] = useState([]);
  const [allDataTags, setAllDataTags] = useState([]); // dynamic tags from DB(s)

  const [filteredData, setFilteredData] = useState([]);
  const [selectedSpotId, setSelectedSpotId] = useState(null);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [showAllSpots, setShowAllSpots] = useState(false);
  const bannerImages = ["/kims.png", "/hunan.png"];
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : true);

  // --- NEW: user geolocation state ---
  const [userLocation, setUserLocation] = useState(null); // {lng, lat}
  const [geoDenied, setGeoDenied] = useState(false);
  const didNearbyInitRef = useRef(false);

  // --- NEW: recenter UI & camera storage ---
  const [showRecenter, setShowRecenter] = useState(false);
  const homeCameraRef = useRef(null);                // stores "my location view"
  const isProgrammaticMoveRef = useRef(false);       // suppress button on scripted moves
  const userLocationRef = useRef(null);              // always-latest user location for listeners

  // region loading flags + viewport extras + live refs
  const [regionLoaded, setRegionLoaded] = useState({ yolo: false, bay: false });
  const [viewportExtras, setViewportExtras] = useState([]);

  // refs
  const allDataRef = useRef(allData);
  const regionLoadedRef = useRef(regionLoaded);
  const fromBannerClickRef = useRef(false);
  const searchMarkerRef = useRef(null);
  const markerMapRef = useRef({});
  const activePopupRef = useRef(null);
  const fromSidebarClickRef = useRef(false);
  const fromMarkerClickRef = useRef(false);
  const manualPopupCloseRef = useRef(false);
  const geocodeDebounceRef = useRef(null);
  const mapContainerRef = useRef(null);
  const headerPadRef = useRef(140); // default; updated after mount
  const userMarkerRef = useRef(null); // NEW

  const location = useLocation();

  useEffect(() => { allDataRef.current = allData; }, [allData]);
  useEffect(() => { regionLoadedRef.current = regionLoaded; }, [regionLoaded]);
  useEffect(() => { userLocationRef.current = userLocation; }, [userLocation]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const defaultView = useMemo(() => ({
    center: isMobile ? [-121.78, 38.55] : [-121.785, 38.5449],
    zoom: isMobile ? 10.2 : 12,
  }), [isMobile]);

  const initCenter = Array.isArray(initialCenter) ? initialCenter : defaultView.center;
  const initZoom = typeof initialZoom === 'number' ? initialZoom : defaultView.zoom;
  const minZoomValue = typeof minZoom === 'number' ? minZoom : 0;

  // Memoized Icons map
  const CATEGORY_ICON = useMemo(() => ({
    Entertainment: (
      <img src="/popcorn-icon.png" alt="Popcorn" style={{ width: 30, height: 30, objectFit: "contain" }} />
    ),
    "Hidden Gems": (
      <img src="/gem-icon.png" alt="Gem" style={{ width: 30, height: 30, objectFit: "contain" }} />
    ),
    Flowers: (
      <img src="/flower-icon.png" alt="Flowers" style={{ width: 30, height: 30, objectFit: "contain" }} />
    ),
    Stargazing: (
      <img src="/star-icon.png" alt="Star" style={{ width: 30, height: 30, objectFit: "contain" }} />
    ),
    Eggheads: (
      <div style={{ display: "flex", alignItems: "center", height: 24 }}>
        <img src="/bookhead.png" alt="Bookhead" style={{ width: 24, height: 24, objectFit: "contain" }} />
      </div>
    ),
    Promoted: (
      <img src="/promoted.webp" alt="Promoted" style={{ width: 30, height: 30, objectFit: "contain" }} />
    ),
  }), []);

  // Early token guard
  const tokenMissing = !mapboxgl.accessToken || mapboxgl.accessToken === "";

  // Map init
  useEffect(() => {
    if (tokenMissing || !mapContainerRef.current) return;

    const mapInstance = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: initCenter,
      zoom: initZoom,
      minZoom: minZoomValue,
      maxZoom: 18,
    });

    const RESTORE_BOUNDS_ZOOM = 8;
    const onZoom = () => {
      if (mapInstance.getZoom() < RESTORE_BOUNDS_ZOOM && mapInstance.getMaxBounds()) {
        mapInstance.setMaxBounds(null);
        mapInstance.setMinZoom(0);
      } else if (mapInstance.getZoom() >= RESTORE_BOUNDS_ZOOM && !mapInstance.getMaxBounds()) {
        mapInstance.setMaxBounds(norCalBounds);
      }
    };

    mapInstance.on("zoom", onZoom);

    // ResizeObserver for container
    const ro = new ResizeObserver(() => mapInstance.resize());
    ro.observe(mapContainerRef.current);

    // Update header padding once
    headerPadRef.current = (document.querySelector("header")?.offsetHeight || 80) + 60;

    // --- NEW: add Geolocate control (optional UI button) ---
    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: false,
      showUserLocation: false,
    });
    mapInstance.addControl(geolocate, "bottom-right");

    setMap(mapInstance);

    const handleMoveEnd = async () => {
      const b = mapInstance.getBounds();
      const viewBBox = [ [b.getWest(), b.getSouth()], [b.getEast(), b.getNorth()] ];

      let newlyFetched = [];

      if (!regionLoadedRef.current.yolo && bboxIntersects(viewBBox, yoloBounds)) {
        const yolo = await fetchYoloPlaces();
        if (yolo.length) {
          const merged = [...allDataRef.current, ...yolo];
          setAllData(merged);
          rebuildDynamicTags(merged);
          newlyFetched = newlyFetched.concat(yolo);
          allDataRef.current = merged;
        }
        setRegionLoaded((s) => ({ ...s, yolo: true }));
      }

      if (!regionLoadedRef.current.bay && bboxIntersects(viewBBox, bayBounds)) {
        const bay = await fetchBayPlaces();
        if (bay.length) {
          const merged = [...allDataRef.current, ...bay];
          setAllData(merged);
          rebuildDynamicTags(merged);
          newlyFetched = newlyFetched.concat(bay);
          allDataRef.current = merged;
        }
        setRegionLoaded((s) => ({ ...s, bay: true }));
      }

      // compute extras visible in viewport
      const extraCandidates = allDataRef.current.filter(
        (s) => (s._source === "yolo_places" || s._source === "bay_places") && s?.lng != null && s?.lat != null && lngLatIn(viewBBox, s.lng, s.lat)
      );
      setViewportExtras(extraCandidates);
    };

    mapInstance.on("moveend", handleMoveEnd);
    mapInstance.once("idle", handleMoveEnd);

    // --- NEW: toggle "Go back to my location" button based on distance from user's location ---
    const RECENTER_KM_THRESHOLD = 1.5;
    const toggleRecenter = () => {
      if (isProgrammaticMoveRef.current) {
        // Don't show the button immediately after our own animation
        isProgrammaticMoveRef.current = false;
        setShowRecenter(false);
        return;
      }
      try {
        const u = userLocationRef.current;
        if (!u) { setShowRecenter(false); return; }
        const c = mapInstance.getCenter();
        const dist = haversineKm({ lng: c.lng, lat: c.lat }, u);
        setShowRecenter(dist > RECENTER_KM_THRESHOLD);
      } catch {
        setShowRecenter(false);
      }
    };
    mapInstance.on("moveend", toggleRecenter);
    mapInstance.on("zoomend", toggleRecenter);

    // Clean-up
    return () => {
      Object.values(markerMapRef.current).forEach((mk) => mk.remove());
      markerMapRef.current = {};
      if (searchMarkerRef.current) {
        searchMarkerRef.current.remove();
        searchMarkerRef.current = null;
      }
      if (userMarkerRef.current) {
        userMarkerRef.current.remove();
        userMarkerRef.current = null;
      }
      mapInstance.off("zoom", onZoom);
      mapInstance.off("moveend", handleMoveEnd);
      mapInstance.off("moveend", toggleRecenter);
      mapInstance.off("zoomend", toggleRecenter);
      try { ro.disconnect(); } catch {}
      mapInstance.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenMissing, initCenter[0], initCenter[1], initZoom, minZoomValue]);

  // --- NEW: request user geolocation on mount (only when no explicit initialCenter & no URL query) ---
  useEffect(() => {
    if (!map) return;
    const params = new URLSearchParams(location.search);
    const hasDeepLink = params.get("hashtags") || params.get("query");
    if (Array.isArray(initialCenter) || hasDeepLink) return; // respect explicit intents

    if (!("geolocation" in navigator)) { setGeoDenied(true); return; }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { longitude: lng, latitude: lat } = pos.coords;
        setUserLocation({ lng, lat });
      },
      (err) => {
        console.warn("Geolocation denied/unavailable:", err?.message);
        setGeoDenied(true);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  }, [map, initialCenter, location.search]);

  // --- NEW: when we have userLocation + data, default to nearby view once ---
  useEffect(() => {
    if (!map || !userLocation || didNearbyInitRef.current === true) return;
    if (!allData || allData.length === 0) return; // wait for base data

    // Build a nearby subset
    const withCoords = allData.filter((s) => Number.isFinite(s?.lng) && Number.isFinite(s?.lat));
    const byDistance = withCoords
      .map((s) => ({ ...s, _distKm: haversineKm(userLocation, { lng: s.lng, lat: s.lat }) }))
      .sort((a, b) => a._distKm - b._distKm);

    const within20km = byDistance.filter((s) => s._distKm <= 20);
    const nearby = (within20km.length > 0 ? within20km : byDistance).slice(0, 25);

    // Place a user marker
    try {
      const el = document.createElement("div");
      el.style.width = "14px";
      el.style.height = "14px";
      el.style.borderRadius = "50%";
      el.style.boxShadow = "0 0 0 4px rgba(24,144,255,0.35)";
      el.style.background = "#1890ff";
      el.title = "You are here";
      userMarkerRef.current?.remove?.();
      userMarkerRef.current = new mapboxgl.Marker(el).setLngLat([userLocation.lng, userLocation.lat]).addTo(map);
    } catch {}

    // Fit bounds to user + nearby spots
    const bounds = new mapboxgl.LngLatBounds();
    bounds.extend([userLocation.lng, userLocation.lat]);
    nearby.forEach((s) => bounds.extend([s.lng, s.lat]));

    map.fitBounds(bounds, {
      padding: { top: headerPadRef.current, bottom: 60, left: 40, right: 50 },
      maxZoom: 25,
      linear: true,
    });

    // Remember this camera as the user's "home" view
    map.once("idle", () => {
      try {
        homeCameraRef.current = {
          center: map.getCenter().toArray(),
          zoom: map.getZoom(),
          bearing: map.getBearing(),
          pitch: map.getPitch(),
        };
      } catch {}
    });

    // Show these in the list by default (without changing filters/tags)
    setFilteredData(nearby);
    setShowAllSpots(true);
    setSelectedCategory(null);

    didNearbyInitRef.current = true; // run only once as the default view
  }, [map, userLocation, allData]);

  // --- NEW: recalc the button immediately when userLocation appears/changes ---
  useEffect(() => {
    if (!map) return;
    const u = userLocation;
    if (!u) { setShowRecenter(false); return; }
    const c = map.getCenter();
    const dist = haversineKm({ lng: c.lng, lat: c.lat }, u);
    setShowRecenter(dist > 1.5);
  }, [map, userLocation]);

  // Fetch base places
  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data, error } = await supabase.from("places").select("*");
      if (!mounted) return;
      if (error) { console.error("Error fetching places:", error); return; }
      const baseData = (data || []).map((s) => ({ ...s, _source: "places" }));
      setAllData(baseData);
      allDataRef.current = baseData;
      rebuildDynamicTags(baseData);
      setShowAllSpots(true);
      setSelectedCategory(null);
      setFilteredData(baseData);
    })();
    return () => { mounted = false; };
  }, []);

  const fetchYoloPlaces = async () => {
    const { data, error } = await supabase.from("yolo_places").select("*");
    if (error) { console.error("Error fetching yolo_places:", error); return []; }
    return (data || []).map((s) => ({ ...s, _source: "yolo_places" }));
  };

  const fetchBayPlaces = async () => {
    const { data, error } = await supabase.from("bay_places").select("*");
    if (error) { console.error("Error fetching bay_places:", error); return []; }
    return (data || []).map((s) => ({ ...s, _source: "bay_places" }));
  };

  // Build tag list from any dataset
  const rebuildDynamicTags = (dataArr) => {
    const tagList = Array.from(new Set((dataArr || []).flatMap(getSpotTags))).filter(Boolean);
    setAllDataTags(tagList);
  };

  // Search handler
  useEffect(() => {
    if (!setSearchHandler) return;
    setSearchHandler.current = async (input) => {
      if (!input || !map) return;

      // Direct coords
      if (Array.isArray(input) && input.length === 2) {
        const [lng, lat] = input; map.flyTo({ center: [lng, lat], zoom: 14, essential: true }); return;
      }
      if (typeof input === 'object' && input.lat != null && input.lng != null) {
        map.flyTo({ center: [input.lng, input.lat], zoom: 14, essential: true }); return;
      }

      const searchQuery = String(input);
      const hashtagTokens = parseHashtagsFromText(searchQuery);
      if (hashtagTokens.length > 0) { applyHashtagSearch(hashtagTokens); return; }

      clearTimeout(geocodeDebounceRef.current);
      geocodeDebounceRef.current = setTimeout(async () => {
        try {
          const res = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?access_token=${mapboxgl.accessToken}`
          );
          const data = await res.json();
          if (data.features?.length) {
            const [lng, lat] = data.features[0].center;
            map.flyTo({ center: [lng, lat], zoom: 14, essential: true });
            if (searchMarkerRef.current) searchMarkerRef.current.remove();
            const el = document.createElement("div");
            el.className = "marker";
            const marker = new mapboxgl.Marker(el)
              .setLngLat([lng, lat])
              .setPopup(new mapboxgl.Popup({ offset: 25 }).setText(data.features[0].place_name))
              .addTo(map);
            marker.togglePopup();
            searchMarkerRef.current = marker;
          } else {
            alert("Location not found!");
          }
        } catch (e) {
          console.error("Geocode failed:", e);
          alert("Search failed. Please try again.");
        }
      }, 250);
    };
  }, [map, setSearchHandler]);

  // URL params init
  useEffect(() => {
    if (!map) return;
    const params = new URLSearchParams(location.search);
    const hashtagsParam = params.get("hashtags");
    const initialQuery = params.get("query");

    if (hashtagsParam) {
      const tokens = parseCommaTokens(hashtagsParam);
      if (tokens.length) setTimeout(() => applyHashtagSearch(tokens), 300);
    } else if (initialQuery) {
      const tokens = parseHashtagsFromText(initialQuery);
      if (tokens.length) setTimeout(() => applyHashtagSearch(tokens), 300);
      else if (setSearchHandler && setSearchHandler.current) setTimeout(() => setSearchHandler.current(initialQuery), 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Interpret & apply hashtag search
  const interpretHashtags = (tokens) => {
    let chosenCategory = null;
    const chosenTags = [];

    const allKnownTags = new Set([
      ...allDataTags,
      ...transportTagOptions.map(norm),
    ]);

    tokens.forEach((raw) => {
      const token = norm(raw);
      if (allKnownTags.has(token) && !chosenTags.includes(token)) { chosenTags.push(token); return; }
      if (categoryAliases[token]) chosenCategory = categoryAliases[token];
      else if (categories.map(norm).includes(token)) chosenCategory = categories.find((c) => norm(c) === token);
    });

    return { chosenCategory, chosenTags };
  };

  const applyHashtagSearch = (tokens) => {
    const { chosenCategory, chosenTags } = interpretHashtags(tokens);
    const catToUse = chosenCategory ?? selectedCategory;
    if (chosenCategory) setSelectedCategory(chosenCategory);
    setSelectedTags(chosenTags);
    setShowAllSpots(false);
    applyFilters(catToUse, chosenTags, false);
  };

  const handleCategoryClick = (cat) => {
    setSelectedCategory(cat);
    setShowAllSpots(false);
    applyFilters(cat, selectedTags, false);
  };

  const handleTagToggle = (tag) => {
    const updatedTags = selectedTags.map(norm).includes(norm(tag))
      ? selectedTags.filter((t) => norm(t) !== norm(tag))
      : [...selectedTags, tag];
    setSelectedTags(updatedTags);
    applyFilters(selectedCategory, updatedTags, showAllSpots);
  };

  const passesFilters = (spot, category, normalizedTags, showAll) => {
    if (!showAll && category && spot.category !== category) return false;
    if (normalizedTags.length > 0) {
      const spotTags = getSpotTags(spot);
      return normalizedTags.every((t) => spotTags.includes(t));
    }
    return true;
  };

  const applyFilters = (category = selectedCategory, tags = selectedTags, showAllOverride = showAllSpots) => {
    const showAll = typeof showAllOverride === "boolean" ? showAllOverride : showAllSpots;
    let result = [...allData];
    if (!showAll && category) result = result.filter((spot) => spot.category === category);

    const normalizedTags = (tags || []).map(norm);
    if (normalizedTags.length > 0) {
      result = result.filter((spot) => {
        const spotTags = getSpotTags(spot);
        return normalizedTags.every((t) => spotTags.includes(t));
      });
    }

    setFilteredData(result);

    if (map) {
      if (result.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        result.forEach((spot) => bounds.extend([spot.lng, spot.lat]));
        map.fitBounds(bounds, {
          padding: { top: headerPadRef.current, bottom: 60, left: 40, right: 160 },
          maxZoom: 15,
          linear: true,
        });
      } else {
        map.flyTo({ center: defaultView.center, zoom: defaultView.zoom, essential: true });
      }
    }
  };

  const handleSpotClick = (spot, source = "sidebar") => {
    const id = spot.id || spot.name;
    fromSidebarClickRef.current = source === "sidebar";
    fromMarkerClickRef.current = source === "marker";
    setSelectedSpotId(id);
    fastFlyTo(map, spot.lng, spot.lat);
    // open popup when available
    requestAnimationFrame(() => {
      const selectedMarker = markerMapRef.current[id];
      const popup = selectedMarker?.getPopup?.();
      if (popup && !popup.isOpen()) popup.addTo(map);
    });
  };

  // --- NEW: recenter handler
  const handleRecenterClick = () => {
    if (!map || !userLocationRef.current) return;
    isProgrammaticMoveRef.current = true;
    if (homeCameraRef.current) {
      map.easeTo({
        center: homeCameraRef.current.center,
        zoom: homeCameraRef.current.zoom,
        bearing: homeCameraRef.current.bearing,
        pitch: homeCameraRef.current.pitch,
        duration: 500,
        offset: CAMERA.offset,
      });
    } else {
      fastFlyTo(map, userLocationRef.current.lng, userLocationRef.current.lat);
    }
  };

  // Lightweight marker motion visual hint
  useEffect(() => {
    if (!map) return;
    const setMarkerMotionMode = (mode) => {
      Object.values(markerMapRef.current).forEach((mk) => {
        const el = mk.getElement?.();
        if (!el) return;
        el.style.willChange = "transform";
        if (mode === "anim") {
          el.dataset._origFilter = el.style.filter || "";
          el.style.filter = "none";
          el.style.opacity = "0.92";
        } else {
          el.style.filter = el.dataset._origFilter || "";
          delete el.dataset._origFilter;
          el.style.opacity = "";
        }
      });
    };

    const onMoveStart = () => setMarkerMotionMode("anim");
    const onMoveEnd = () => setMarkerMotionMode("rest");

    map.on("movestart", onMoveStart);
    map.on("moveend", onMoveEnd);

    return () => {
      map.off("movestart", onMoveStart);
      map.off("moveend", onMoveEnd);
    };
  }, [map]);

  // Marker diffing & popup creation
  useEffect(() => {
    if (!map) return;

    const byId = (s) => s.id || s.name;
    const normalizedTags = (selectedTags || []).map(norm);

    // Only include extras that match current filters
    const extrasFiltered = (viewportExtras || []).filter((s) => passesFilters(s, selectedCategory, normalizedTags, showAllSpots));

    const combined = [...filteredData, ...extrasFiltered].reduce((acc, spot) => {
      const id = byId(spot);
      if (!acc.map.has(id)) { acc.map.set(id, true); acc.list.push(spot); }
      return acc;
    }, { map: new Map(), list: [] }).list;

    const nextIds = new Set(combined.map(byId));

    // Remove orphans
    Object.keys(markerMapRef.current).forEach((id) => {
      if (!nextIds.has(id)) {
        markerMapRef.current[id].remove();
        delete markerMapRef.current[id];
      }
    });

    // Upsert markers
    combined.forEach((spot) => {
      const id = byId(spot);
      let marker = markerMapRef.current[id];
      if (!marker) {
        const el = document.createElement("img");
        el.alt = "Location marker";
        el.style.width = "45px";
        el.style.height = "45px";
        el.style.objectFit = "contain";
        el.style.filter = "drop-shadow(10px 10px 35px rgba(69, 29, 0, 0.27))";

        el.addEventListener("click", () => {
          if (spot.name === "Kims Mart" || spot.name === "Hunan Bar & Restaurant") {
            if (window.gtag) {
              window.gtag("event", "marker_click", { spot_name: spot.name, category: spot.category });
            }
          }
          setSelectedSpotId(null);
          setTimeout(() => handleSpotClick(spot, "marker"), 50);
        });

        const popup = new mapboxgl.Popup({ offset: 25 });
        popup.on("open", () => { activePopupRef.current = popup; });
        popup.on("close", () => {
          if (activePopupRef.current === popup) {
            const popupId = selectedSpotId;
            activePopupRef.current = null;
            setTimeout(() => {
              const isPopupStillSame = markerMapRef.current[popupId]?.getPopup() === activePopupRef.current;
              const noClickTriggered = !fromSidebarClickRef.current && !fromMarkerClickRef.current && !fromBannerClickRef.current;
              if ((noClickTriggered || manualPopupCloseRef.current) && !isPopupStillSame) {
                setSelectedSpotId(null);
                map?.flyTo({ center: defaultView.center, zoom: defaultView.zoom, essential: true });
              }
              fromSidebarClickRef.current = false;
              fromMarkerClickRef.current = false;
              fromBannerClickRef.current = false;
              manualPopupCloseRef.current = false;
            }, 150);
          }
        });

        marker = new mapboxgl.Marker(el).setLngLat([spot.lng, spot.lat]).setPopup(popup).addTo(map);
        markerMapRef.current[id] = marker;
      } else {
        marker.setLngLat([spot.lng, spot.lat]);
      }

      // Update icon
      const el = marker.getElement();
      if (el && el.tagName === "IMG") {
        let markerSrc;
        if (spot.category === "Eggheads") {
          const eggheadMarkers = {
            "Egghead (Bookhead)": "/bookhead.png",
            "EggHead (Stargazer)": "/stargazing.png",
            "EggHead (Eye on Mrak)": "/mrak.png",
            "EggHead (See No Evil/Hear No Evil)": "/evil.png",
            "EggHead (Yin & Yang)": "/yinyang.png",
          };
          markerSrc = eggheadMarkers[spot.name] || "/bookhead.png";
        } else if ((spot.id || spot.name) === selectedSpotId) {
          markerSrc = categorySelectedMarkerMap[spot.category] || "/marker4.png";
        } else {
          markerSrc = categoryMarkerMap[spot.category] || "/newmarker.png";
        }
        if (el.src !== markerSrc) el.src = markerSrc;
      }

      // Update popup HTML (sanitized)
      const addr = (spot.address ?? "").trim();
      const isUrl = /^https?:\/\//i.test(addr);
      let googleHref;
      if (spot.place_id) {
        googleHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(spot.name || "")}&query_place_id=${spot.place_id}`;
      } else if (isUrl) {
        googleHref = addr;
      } else if (addr) {
        googleHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
      } else {
        googleHref = `https://www.google.com/maps/search/?api=1&query=${spot.lat},${spot.lng}`;
      }

      const html = `<div>
        <strong>${escapeHTML(spot.name)}</strong><br />
        <p style="margin: 0.5rem 0; font-size: 0.9rem;">${escapeHTML(spot.description || "No description available.")}</p>
        <a href="${googleHref}"
           target="_blank"
           rel="noopener noreferrer"
           style="display:inline-block;margin-top:0.5rem;background:#81d1e8;color:white;padding:0.3rem 0.6rem;border-radius:4px;text-decoration:none;font-size:0.85rem;">
           Open in Google Maps
        </a>
      </div>`;
      const popup = marker.getPopup();
      if (popup && popup._content?.innerHTML !== html) popup.setHTML(html);
    });

    // if a different selection now, ensure non-selected popups are closed
    Object.entries(markerMapRef.current).forEach(([id, marker]) => {
      if (id !== (selectedSpotId || "")) marker.getPopup()?.remove();
    });

    if (selectedSpotId && markerMapRef.current[selectedSpotId]) {
      const marker = markerMapRef.current[selectedSpotId];
      const popup = marker.getPopup();
      if (popup && !popup.isOpen()) popup.addTo(map);
    }

  }, [map, filteredData, viewportExtras, selectedSpotId, selectedTags, selectedCategory, showAllSpots, defaultView.center, defaultView.zoom]);

  const handleBannerClick = () => {
    const currentImage = bannerImages[currentBannerIndex];
    if (window.gtag) {
      window.gtag("event", "banner_click", { banner_image: currentImage, banner_index: currentBannerIndex });
    }
    let spotName = null;
    if (currentImage.includes("kims")) spotName = "Kims Mart";
    else if (currentImage.includes("hunan")) spotName = "Hunan Bar & Restaurant";
    if (!spotName || allData.length === 0 || !map) return;
    const matchedSpot = allData.find((spot) => spot.name === spotName);
    if (matchedSpot) { fromBannerClickRef.current = true; handleSpotClick(matchedSpot, "banner"); }
  };

  const renderSidebarContent = () => (
    <div className="bottom-sheet-content">
      {!selectedCategory ? (
        <>
          <h2 className="sidebar-title">Where do you want to go?</h2>

          {/* NEW: simple status when geolocation is denied/unavailable */}
          {geoDenied && (
            <div className="spot-warning" style={{ marginBottom: 8 }}>
              ⚠️ Location access is off. We'll show a general view instead.
            </div>
          )}

          <div className="toggle-container">
            <input
              type="checkbox"
              id="showAllToggle"
              checked={showAllSpots}
              onChange={() => {
                const toggled = !showAllSpots;
                setShowAllSpots(toggled);
                if (toggled) {
                  setSelectedCategory(null);
                  applyFilters(null, selectedTags, true);
                  map?.flyTo({ center: defaultView.center, zoom: defaultView.zoom });
                } else {
                  const stevenson = allData.find((spot) => spot.name === "Stevenson Bridge");
                  const eggheads = allData.filter((spot) => spot.category === "Eggheads");
                  setSelectedCategory(null);
                  setFilteredData([...(stevenson ? [stevenson] : []), ...eggheads]);
                  map?.flyTo({ center: defaultView.center, zoom: defaultView.zoom });
                }
              }}
            />
            <label htmlFor="showAllToggle" className="toggle-label">Show all locations</label>
          </div>

          <div className="button-card-list">
            {categories.map((cat) => (
              <div
                key={cat}
                className={`card-button ${selectedCategory === cat ? "active-card" : ""}`}
                onClick={() => { handleCategoryClick(cat); setShowAllSpots(false); }}
              >
                <div className="card-icon">{CATEGORY_ICON[cat] || "📍"}</div>
                <div className="card-label">{cat}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {isMobile ? (
            <div className="floating-back-button-container">
              <button
                className="back-button"
                onClick={() => {
                  setSelectedCategory(null);
                  const stevenson = allData.find((spot) => spot.name === "Stevenson Bridge");
                  const eggheads = allData.filter((spot) => spot.category === "Eggheads");
                  setFilteredData([...(stevenson ? [stevenson] : []), ...eggheads]);
                  setSelectedSpotId(null);
                  map?.flyTo({ center: defaultView.center, zoom: defaultView.zoom });
                }}
              >
                ← Back
              </button>
            </div>
          ) : (
            <div className="sidebar-header-desktop">
              <button
                className="back-button"
                onClick={() => {
                  setSelectedCategory(null);
                  const stevenson = allData.find((spot) => spot.name === "Stevenson Bridge");
                  const eggheads = allData.filter((spot) => spot.category === "Eggheads");
                  setFilteredData([...(stevenson ? [stevenson] : []), ...eggheads]);
                  setSelectedSpotId(null);
                  map?.flyTo({ center: defaultView.center, zoom: defaultView.zoom });
                }}
              >
                ← Back
              </button>
              <h2 className="sidebar-title">{selectedCategory}</h2>
            </div>
          )}

          {/* Category-specific notes */}
          {selectedCategory === "Stargazing" && (
            <div className="spot-warning stargazing-warning">
              🌙 Stargazing Safety Reminder: Some locations may be remote or poorly lit. Always be aware of your surroundings and bring a friend if you can!
            </div>
          )}

          <div className="spot-list">
            {selectedCategory === "Promoted" ? (
              <div className="promo-grid">
                {["Hunan Bar & Restaurant", "Kims Mart"].map((name) => {
                  const spot = filteredData.find((s) => s.name === name) || allData.find((s) => s.name === name);
                  if (!spot) return null;
                  const imgSrc = promotedCardImages[name];
                  return (
                    <button key={name} className="promo-card" onClick={() => handleSpotClick(spot)} aria-label={name}>
                      <img className="promo-card-img" src={imgSrc} alt={name} />
                      <span className="promo-card-label">{name}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              filteredData.map((spot, idx) => (
                <div key={idx} className="spot-card" onClick={() => handleSpotClick(spot)}>
                  <strong>{spot.name}</strong>
                  <p>{spot.description}</p>
                </div>
              ))
            )}
          </div>
        </>
      )}

      <button className="text-button" onClick={() => window.open("https://forms.gle/kioPS6GUkbFyY6vZ9", "_blank")}>
        Want to see your own spot on the map? Click here!
      </button>
      <button className="text-button" onClick={() => window.open("https://forms.gle/rKazR4WUyZzLavXQ8", "_blank")}>
        Want a Pioneer Map for a different city? Click here!
      </button>
      <button className="text-button" onClick={() => window.open("https://forms.gle/8F3o8UTQvznu2vVj6", "_blank")}>
        Recommend a name for our penguin mascot!
      </button>
      <a href="https://instagram.com/pioneer_official_1" target="_blank" rel="noopener noreferrer" className="instagram-button">
        <img src="/Instagram-icon.png" alt="Instagram" className="instagram-icon" />
        Our Instagram
      </a>
      <p>Contact us: officialpioneer0430@gmail.com</p>
    </div>
  );

  if (tokenMissing) {
    return (
      <div className="app-container">
        <div className="map-wrapper">
          <div className="map-overlay-container">
            <div className="map-container token-missing">
              <p>Mapbox token missing. Please set <code>REACT_APP_MAPBOX_TOKEN</code>.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return compact ? (
    <div className="compact-embed">
      <div className="map-overlay-container">
        <div ref={mapContainerRef} className="map-container" />
        {/* Recenter button overlay in compact too */}
        {showRecenter && (
          <button
            className="recenter-btn"
            onClick={handleRecenterClick}
            aria-label="Go back to my location"
          >
            ◀︎ Go back to my location
          </button>
        )}
      </div>
    </div>
  ) : (
    <div className="app-container">
      {/* Optional banner (kept commented) */}
      {false && (
        isMobile ? (
          <div className="ad-banner-mobile-container">
            <div className="ad-banner-clickable" onClick={handleBannerClick} style={{ cursor: "pointer" }}>
              <img src={bannerImages[currentBannerIndex]} alt="Ad Banner" className="ad-banner-mobile" />
            </div>
          </div>
        ) : (
          <div onClick={handleBannerClick} style={{ cursor: "pointer" }}>
            <img src={bannerImages[currentBannerIndex]} alt="Ad Banner" className="ad-banner-desktop" />
          </div>
        )
      )}

      {isMobile && isHelpModalOpen && (
        <div className="help-modal-overlay">
          <div className="help-modal">
            <button className="close-button" onClick={() => setIsHelpModalOpen(false)}>×</button>
            <h2>Not sure how to use our map?</h2>
            <p>Select a location and click open on google maps to route yourself</p>
            <p>Have fun pioneering 😊</p>
            <p>Contact us: officialpioneer0430@gmail.com</p>
          </div>
        </div>
      )}

      <div className="map-wrapper">
        {/* Desktop sidebar (left) */}
        {!isMobile && <div className="sidebar styled-sidebar">{renderSidebarContent()}</div>}

        <div className="map-overlay-container">
          {isMobile ? (
            <div className="floating-filter-chips">
              {transportTagOptions.map((tag) => (
                <button
                  key={tag}
                  className={`filter-chip ${selectedTags.map(norm).includes(norm(tag)) ? "active" : ""}`}
                  onClick={() => setIsHelpModalOpen(tag === 'help' ? true : (handleTagToggle(tag), false))}
                >
                  {tag === "car" && "🚗 Car"}
                  {tag === "bike" && "🚴️ Bike"}
                  {tag === "walking" && "🚶️ Walking"}
                </button>
              ))}
              <button className="help-button map-help-button" onClick={() => setIsHelpModalOpen(true)}>?</button>
            </div>
          ) : (
            <div className="floating-filter-chips">
              {transportTagOptions.map((tag) => (
                <button
                  key={tag}
                  className={`filter-chip ${selectedTags.map(norm).includes(norm(tag)) ? "active" : ""}`}
                  onClick={() => handleTagToggle(tag)}
                >
                  {tag === "car" && "🚗 Car"}
                  {tag === "bike" && "🚴️ Bike"}
                  {tag === "walking" && "🚶️ Walking"}
                </button>
              ))}
            </div>
          )}

          <div ref={mapContainerRef} className="map-container" />

          {/* --- NEW: Recenter button overlay --- */}
          {showRecenter && (
            <button
              className="recenter-btn"
              onClick={handleRecenterClick}
              aria-label="Go back to my location"
            >
              ◀︎ Go back to my location
            </button>
          )}
        </div>

        {/* Mobile bottom sheet OR right sidebar */}
        {isMobile ? (
          <BottomSheet
            open={true}
            defaultSnap={({ maxHeight }) => maxHeight * 0.25}
            snapPoints={({ maxHeight }) => [0.25, 0.5, 0.9].map((p) => maxHeight * p)}
            blocking={false}
            expandOnContentDrag
            scrollLocking={true}
          >
            {renderSidebarContent()}
          </BottomSheet>
        ) : (
          <div className="sidebar styled-sidebar">{renderSidebarContent()}</div>
        )}
      </div>
      <Analytics />
    </div>
  );
}
